import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';
import GuideCTA from '../components/GuideCTA';

const GuideOptimiserCVATS = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Optimiser son CV pour les ATS: modèle et bonnes pratiques</title>
                <meta name="description" content="Comprenez le fonctionnement des ATS et apprenez à optimiser votre CV: structure, mots-clés, formatage, exemples et template compatible ATS." />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/optimiser-cv-ats" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {"@type": "ListItem","position": 1,"name": "Accueil","item": "https://www.cv-analyse.fr/"},
                    {"@type": "ListItem","position": 2,"name": "Optimiser CV ATS","item": "https://www.cv-analyse.fr/guide/optimiser-cv-ats"}
                  ]
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": "Optimiser son CV pour les ATS",
                  "description": "Comprendre et optimiser son CV pour les ATS",
                  "author": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "publisher": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "dateModified": "2026-08-15",
                  "mainEntityOfPage": {"@type": "WebPage", "@id": "https://www.cv-analyse.fr/guide/optimiser-cv-ats"}
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment optimiser son CV pour les ATS",
                  "description": "Passez les filtres des logiciels ATS et augmentez vos chances d'être sélectionné grâce à un CV correctement optimisé.",
                  "step": [
                    {"@type": "HowToStep", "name": "Comprendre le fonctionnement des ATS", "text": "Les ATS scannent et notent les CV automatiquement avant qu'un humain ne les lise."},
                    {"@type": "HowToStep", "name": "Analyser l'offre et extraire les mots-clés", "text": "Identifiez les compétences et termes clés mentionnés dans l'annonce d'emploi."},
                    {"@type": "HowToStep", "name": "Structurer le CV en sections standardisées", "text": "Utilisez des titres de sections classiques : Expériences, Formation, Compétences, Langues."},
                    {"@type": "HowToStep", "name": "Utiliser un format compatible ATS", "text": "Évitez les tableaux, colonnes multiples, images et zones de texte qui perturbent la lecture ATS."},
                    {"@type": "HowToStep", "name": "Tester son score ATS", "text": "Utilisez CV Analyse pour mesurer votre compatibilité ATS et identifier les points à corriger."}
                  ]
                }
                `}</script>
            </Helmet>

            {/* Fil d'Ariane */}
            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600" itemScope itemType="https://schema.org/BreadcrumbList">
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/" itemProp="item" className="hover:text-slate-900">
                                <span itemProp="name">Accueil</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </li>
                        <li className="text-slate-400">/</li>
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <span itemProp="name" className="text-slate-900">Optimiser CV ATS</span>
                            <meta itemProp="position" content="2" />
                        </li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">⚡</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                    Optimiser son CV pour les ATS
                </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Passez les filtres automatiques des recruteurs avec une structure et des mots-clés optimisés
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="optimiser-cv-ats" />

            {/* Table des matières */}
            <div className="card-surface mx-4 mt-4 max-w-4xl mx-auto">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Table des matières</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a href="#comprendre" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">1</span>
                            <span className="text-gray-700">Comprendre les ATS</span>
                        </a>
                        <a href="#structure" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">2</span>
                            <span className="text-gray-700">Structure optimale</span>
                        </a>
                        <a href="#mots-cles" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">3</span>
                            <span className="text-gray-700">Mots-clés stratégiques</span>
                        </a>
                        <a href="#formatage" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">4</span>
                            <span className="text-gray-700">Formatage compatible</span>
                        </a>
                    </div>
                </div>
            </div>

            <article className="px-6 py-16 mx-auto max-w-5xl">

                {/* Section 1: Comprendre les ATS */}
                <div id="comprendre" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🔍</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Comprendre les ATS</h2>
                        </div>
                        
                        <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-2xl p-6 mb-6">
                            <h3 className="text-xl font-bold text-green-900 mb-3">💡 Qu'est-ce qu'un ATS ?</h3>
                            <p className="text-gray-700">Les ATS (Applicant Tracking Systems) sont des logiciels que beaucoup d&apos;entreprises, surtout les plus grandes, utilisent pour lire et filtrer les CV avant qu&apos;un recruteur ne les ouvre.</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-3">📊 Analyse automatique</h3>
                                <p className="text-gray-700 text-sm">L'ATS scanne votre CV et extrait les informations clés (compétences, expériences, formation).</p>
                            </div>
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-3">🎯 Score de correspondance</h3>
                                <p className="text-gray-700 text-sm">Il compare votre CV à l'offre d'emploi et attribue un score de compatibilité.</p>
                            </div>
                            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-orange-900 mb-3">⚡ Filtrage rapide</h3>
                                <p className="text-gray-700 text-sm">Seuls les CV avec un score élevé sont transmis aux recruteurs humains.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Structure optimale */}
                <div id="structure" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">📋</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Structure compatible ATS</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="flex items-start p-4 bg-green-50 rounded-xl border-l-4 border-green-500">
                                    <span className="text-green-500 text-xl mr-4">✅</span>
                                    <div>
                                        <h3 className="font-bold text-green-900">Sections standardisées</h3>
                                        <p className="text-green-700 text-sm">Résumé, Expériences, Compétences, Formation</p>
                                    </div>
                                </div>
                                <div className="flex items-start p-4 bg-green-50 rounded-xl border-l-4 border-green-500">
                                    <span className="text-green-500 text-xl mr-4">✅</span>
                                    <div>
                                        <h3 className="font-bold text-green-900">Police simple</h3>
                                        <p className="text-green-700 text-sm">Arial, Roboto, Calibri - pas de polices fantaisistes</p>
                                    </div>
                                </div>
                                <div className="flex items-start p-4 bg-green-50 rounded-xl border-l-4 border-green-500">
                                    <span className="text-green-500 text-xl mr-4">✅</span>
                                    <div>
                                        <h3 className="font-bold text-green-900">PDF texte</h3>
                                        <p className="text-green-700 text-sm">Export en PDF texte, pas d'image scannée</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                                <h3 className="font-bold text-gray-900 mb-4">📝 Exemple de structure</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-blue-500">
                                        <strong>RÉSUMÉ PROFESSIONNEL</strong><br/>
                                        <span className="text-gray-600">Développeur Full-Stack avec 5 ans d'expérience...</span>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                                        <strong>EXPÉRIENCE PROFESSIONNELLE</strong><br/>
                                        <span className="text-gray-600">Développeur Senior - TechCorp (2020-2023)</span>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-purple-500">
                                        <strong>COMPÉTENCES</strong><br/>
                                        <span className="text-gray-600">React, Node.js, JavaScript, Python...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Mots-clés stratégiques */}
                <div id="mots-cles" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🎯</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Mots-clés stratégiques</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-purple-900 mb-3">🔍 Analyse de l'offre</h3>
                                    <p className="text-gray-700 text-sm mb-4">Repérez les mots-clés dans l'offre d'emploi :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Compétences techniques (React, Python, SQL)</li>
                                        <li>• Outils et technologies</li>
                                        <li>• Secteur d'activité</li>
                                        <li>• Niveau d'expérience</li>
                    </ul>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-orange-900 mb-3">⚡ Intégration naturelle</h3>
                                <p className="text-gray-700 text-sm mb-4">Évitez le bourrage de mots-clés :</p>
                                <div className="space-y-3">
                                    <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                                        <p className="text-red-700 text-sm italic">❌ "React React JavaScript Python SQL"</p>
                                    </div>
                                    <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                                        <p className="text-green-700 text-sm">✅ "Développé des applications web avec React et Node.js"</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>

                {/* Section 4: Formatage compatible */}
                <div id="formatage" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⚙️</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Formatage compatible</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="flex items-start p-4 bg-green-50 rounded-xl">
                                    <span className="text-green-500 text-xl mr-4">✅</span>
                                    <div>
                                        <h3 className="font-bold text-green-900">Puces et listes</h3>
                                        <p className="text-green-700 text-sm">Utilisez des puces pour structurer vos réalisations</p>
                                    </div>
                                </div>
                                <div className="flex items-start p-4 bg-green-50 rounded-xl">
                                    <span className="text-green-500 text-xl mr-4">✅</span>
                                    <div>
                                        <h3 className="font-bold text-green-900">Format simple</h3>
                                        <p className="text-green-700 text-sm">Évitez les tableaux et éléments graphiques</p>
                                    </div>
                                </div>
                                <div className="flex items-start p-4 bg-green-50 rounded-xl">
                                    <span className="text-green-500 text-xl mr-4">✅</span>
                                    <div>
                                        <h3 className="font-bold text-green-900">Hiérarchie claire</h3>
                                        <p className="text-green-700 text-sm">Titres, sous-titres et paragraphes bien structurés</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                                <h3 className="font-bold text-gray-900 mb-4">📋 Checklist formatage</h3>
                                <div className="space-y-3">
                                    <div className="flex items-center">
                                        <input type="checkbox" className="mr-3" />
                                        <span className="text-sm text-gray-700">Police standard (Arial, Calibri)</span>
                                    </div>
                                    <div className="flex items-center">
                                        <input type="checkbox" className="mr-3" />
                                        <span className="text-sm text-gray-700">Pas de colonnes complexes</span>
                                    </div>
                                    <div className="flex items-center">
                                        <input type="checkbox" className="mr-3" />
                                        <span className="text-sm text-gray-700">Puces simples (•, -, *)</span>
                                    </div>
                                    <div className="flex items-center">
                                        <input type="checkbox" className="mr-3" />
                                        <span className="text-sm text-gray-700">Pas d'images ou graphiques</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>

                {/* CTA Section */}
                <GuideCTA
                    title="Votre CV passe-t-il les filtres ATS ?"
                    description="Découvrez vos mots-clés manquants et votre score de compatibilité ATS en 2 minutes — score et 3 conseils offerts."
                    ctaText="Tester mon CV pour les ATS"
                    theme="green"
                />

                {/* Navigation entre guides */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/guide/analyse-cv-ia" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-blue-700">Guide précédent</h3>
                            <p className="text-sm text-gray-600">Analyse de CV par IA</p>
                        </div>
                    </Link>
                    
                    <Link to="/guides" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-green-700">Retour aux guides</h3>
                            <p className="text-sm text-gray-600">Voir tous nos guides CV</p>
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center ml-4 group-hover:bg-green-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </div>
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideOptimiserCVATS;


